import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import { Card, CardContent } from '../components/ui/card';
import { PublicLayout } from '../components/landing/PublicLayout';
import { Input } from '../components/ui/input';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Search, BookOpen, Swords, Mail, ArrowRight } from 'lucide-react';

export default function HelpPage() {
  const { language } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');

  const faqCategories = [
    {
      title: t('help_category_getting_started', language),
      faqs: [1, 2].map((n) => ({
        question: t(`help_faq_${n}_q`, language),
        answer: t(`help_faq_${n}_a`, language),
      })),
    },
    {
      title: t('help_category_campaigns', language),
      faqs: [3, 4, 5].map((n) => ({
        question: t(`help_faq_${n}_q`, language),
        answer: t(`help_faq_${n}_a`, language),
      })),
    },
    {
      title: t('help_category_technical', language),
      faqs: [6, 7, 8].map((n) => ({
        question: t(`help_faq_${n}_q`, language),
        answer: t(`help_faq_${n}_a`, language),
      })),
    },
  ];

  const filteredCategories = searchQuery.trim()
    ? faqCategories
        .map((cat) => ({
          ...cat,
          faqs: cat.faqs.filter(
            (faq) =>
              faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
              faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
          ),
        }))
        .filter((cat) => cat.faqs.length > 0)
    : faqCategories;

  const quickLinks = [
    {
      icon: BookOpen,
      title: language === 'fr' ? 'Guide de démarrage' : 'Getting Started Guide',
      desc: language === 'fr' ? 'Apprenez les bases en quelques minutes.' : 'Learn the basics in minutes.',
      to: '/features',
      color: 'text-blue-400',
    },
    {
      icon: Swords,
      title: language === 'fr' ? 'Fonctionnalités' : 'Features',
      desc: language === 'fr' ? 'Découvrez tout ce que WyrdMaker peut faire.' : 'Discover everything WyrdMaker can do.',
      to: '/features',
      color: 'text-red-400',
    },
    {
      icon: Mail,
      title: language === 'fr' ? 'Nous contacter' : 'Contact Us',
      desc: language === 'fr' ? 'Besoin d\'aide personnalisée ?' : 'Need personalized help?',
      to: '/contact',
      color: 'text-green-400',
    },
  ];

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="py-16 sm:py-24 px-4 text-center relative">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-background to-background dark:from-primary/8" />
        <div className="relative z-10 max-w-3xl mx-auto">
          <h1 className="font-cinzel text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            {t('help_page_title', language)}
          </h1>
          <p className="text-muted-foreground text-base sm:text-lg mb-8">
            {t('help_page_subtitle', language)}
          </p>

          {/* Search */}
          <div className="max-w-md mx-auto relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('help_search_placeholder', language)}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="pb-16 sm:pb-20 px-4">
        <div className="max-w-2xl mx-auto space-y-10">
          {filteredCategories.map((category, ci) => (
            <div key={ci}>
              <h2 className="font-cinzel font-bold text-xl mb-4">{category.title}</h2>
              <Accordion type="single" collapsible className="w-full">
                {category.faqs.map((faq, fi) => (
                  <AccordionItem key={fi} value={`cat-${ci}-faq-${fi}`}>
                    <AccordionTrigger className="text-left text-sm sm:text-base font-medium">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}

          {filteredCategories.length === 0 && (
            <p className="text-center text-muted-foreground py-8">
              {language === 'fr' ? 'Aucun résultat trouvé.' : 'No results found.'}
            </p>
          )}
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-16 sm:py-20 px-4 bg-secondary/10">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-cinzel font-bold text-2xl text-center mb-10">
            {t('help_quick_links_title', language)}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {quickLinks.map((link, i) => {
              const Icon = link.icon;
              return (
                <Link key={i} to={link.to}>
                  <Card className="h-full border-border/50 hover:border-primary/30 transition-colors group cursor-pointer">
                    <CardContent className="p-5">
                      <Icon className={`h-6 w-6 ${link.color} mb-3`} />
                      <h3 className="font-cinzel font-bold text-sm mb-1 group-hover:text-primary transition-colors">
                        {link.title}
                      </h3>
                      <p className="text-xs text-muted-foreground">{link.desc}</p>
                      <ArrowRight className="h-4 w-4 text-muted-foreground mt-3 group-hover:text-primary group-hover:translate-x-1 transition-all" />
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
